import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * Detects a vigorous shake using DeviceMotion. Returns:
 *  - supported: bool (true if the API exists at all)
 *  - permission: 'granted' | 'denied' | 'unknown' | 'unavailable'
 *  - request(): user-gesture-initiated permission prompt (iOS 13+ requires it)
 *  - onShake: subscribe callback (intensity 0..3)
 *
 * Threshold tuned for a deliberate shake without false-firing while walking.
 */
export const useShake = ({ enabled = true, threshold = 18, cooldownMs = 800 } = {}) => {
  const lastTriggerRef = useRef(0);
  const handlersRef = useRef(new Set());
  const [permission, setPermission] = useState(
    typeof DeviceMotionEvent === 'undefined' ? 'unavailable' : 'unknown'
  );

  const supported = typeof window !== 'undefined' && typeof DeviceMotionEvent !== 'undefined';

  const onShake = useCallback((fn) => {
    handlersRef.current.add(fn);
    return () => handlersRef.current.delete(fn);
  }, []);

  const request = useCallback(async () => {
    if (!supported) return 'unavailable';
    try {
      // iOS 13+ requires explicit permission
      if (typeof DeviceMotionEvent.requestPermission === 'function') {
        const p = await DeviceMotionEvent.requestPermission();
        setPermission(p);
        return p;
      }
      setPermission('granted');
      return 'granted';
    } catch {
      setPermission('denied');
      return 'denied';
    }
  }, [supported]);

  useEffect(() => {
    if (!enabled || !supported || permission === 'denied' || permission === 'unavailable') return;
    const handler = (e) => {
      const a = e.accelerationIncludingGravity || e.acceleration;
      if (!a) return;
      const mag = Math.sqrt((a.x || 0) ** 2 + (a.y || 0) ** 2 + (a.z || 0) ** 2);
      const now = Date.now();
      if (mag > threshold && now - lastTriggerRef.current > cooldownMs) {
        lastTriggerRef.current = now;
        const intensity = Math.min(3, (mag - threshold) / 6);
        handlersRef.current.forEach((fn) => fn(intensity));
      }
    };
    window.addEventListener('devicemotion', handler);
    return () => window.removeEventListener('devicemotion', handler);
  }, [enabled, supported, permission, threshold, cooldownMs]);

  return { supported, permission, request, onShake };
};

/**
 * Detects a sustained "blow" via microphone — looks for high low-frequency
 * energy that's typical of breath on the mic. Returns:
 *  - start(): begin listening (requires user gesture for getUserMedia)
 *  - stop(): release the mic
 *  - onBlow: subscribe (intensity 0..3)
 *  - listening: bool
 */
export const useBlow = ({ threshold = 110, cooldownMs = 1200 } = {}) => {
  const ctxRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const lastTriggerRef = useRef(0);
  const handlersRef = useRef(new Set());
  const [listening, setListening] = useState(false);

  const onBlow = useCallback((fn) => {
    handlersRef.current.add(fn);
    return () => handlersRef.current.delete(fn);
  }, []);

  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (ctxRef.current) {
      try { ctxRef.current.close(); } catch {}
      ctxRef.current = null;
    }
    setListening(false);
  }, []);

  const start = useCallback(async () => {
    if (listening) return true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      src.connect(analyser);
      ctxRef.current = ctx;
      streamRef.current = stream;
      const buf = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(buf);
        // average of low-band bins 2..30 (~80Hz..1kHz) = breath sweet spot
        let sum = 0;
        const lo = 2, hi = 30;
        for (let i = lo; i < hi; i++) sum += buf[i];
        const lowAvg = sum / (hi - lo);
        const now = Date.now();
        if (lowAvg > threshold && now - lastTriggerRef.current > cooldownMs) {
          lastTriggerRef.current = now;
          const intensity = Math.min(3, (lowAvg - threshold) / 30);
          handlersRef.current.forEach((fn) => fn(intensity));
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
      setListening(true);
      return true;
    } catch {
      setListening(false);
      return false;
    }
  }, [listening, threshold, cooldownMs]);

  useEffect(() => () => stop(), [stop]);

  return { start, stop, onBlow, listening };
};
